import type { ImportedKeyboard } from "@/hardware/keyboards/keyboard-import";
import { SC2_SOURCES } from "@/platform/catalog/sc2-sources";
import { usePersistentState } from "@/platform/storage/persistent-state";
import { type KeyValueStore, storageKey } from "@/platform/storage/storage";
import {
  isImportedRecord,
  isStr,
  isStrRecord,
  isUpload,
  isUploadRecord,
} from "@/platform/storage/validators";

// the persisted slice of page state. The selection ids restore "where I left off";
// the uploads and per-board layer carry their durable truth (a Profile's text+name,
// a board's decoded ImportedKeyboard). Compare A/B state is deliberately transient, not persisted.
const PKEY = {
  boards: storageKey("boards"),
  layoutSel: storageKey("layout-sel"),
  profile: storageKey("profile"),
  profileSel: storageKey("profile-sel"),
  boardId: storageKey("board-id"),
  gameId: storageKey("game-id"),
  sourceId: storageKey("source-id"),
  mode: storageKey("card-mode"),
  race: storageKey("card-race"),
  context: storageKey("card-context"),
  flatContext: storageKey("flat-context"),
  profileUpload: storageKey("profile-upload"),
};

// the built-in that anchors a fresh session and the fallback when a persisted boardId no longer
// resolves (its boards blob was evicted, or a built-in was removed between versions). A returning
// visitor keeps their persisted board; this only sets a brand-new visitor's starting board.
export const DEFAULT_BOARD_ID = "ansi-tkl";

/**
 * Every persisted slice of the visualizer page, behind one hook so the main
 * component reads "where I left off" as a single concern. Each value mirrors a localStorage key
 * through usePersistentState with a type-predicate validator; a corrupt or stale blob falls back to
 * its default. Compare A/B state is deliberately NOT here - it is transient (seeded from a share
 * link, never saved).
 */
export function usePageState(storage: KeyValueStore) {
  const [boardId, setBoardId] = usePersistentState(storage, PKEY.boardId, DEFAULT_BOARD_ID, isStr);
  // the active Game: SC2 is contextual and lands by default; Destiny 2 and Deadlock are
  // simple static-Profile games.
  const [gameId, setGameId] = usePersistentState(storage, PKEY.gameId, "sc2", isStr);
  // which SC2 source the Profile picker has selected: a built-in setup id (TheCore right/left) or
  // "uploaded". A stale id derives back to the landing default.
  const [sc2SourceId, setSc2SourceId] = usePersistentState(
    storage,
    PKEY.sourceId,
    SC2_SOURCES[0]?.id ?? "",
    isStr,
  );
  // the simple-Game Profile selection, per Game (Record<gameId, profileId>). Switching Default <->
  // Edennil restores where you left off; a stale id derives back to the landing default.
  const [profileSel, setProfileSel] = usePersistentState<Record<string, string>>(
    storage,
    PKEY.profileSel,
    {},
    isStrRecord,
  );
  const [imported, setImported] = usePersistentState<Record<string, ImportedKeyboard>>(
    storage,
    PKEY.boards,
    {},
    isImportedRecord,
  );
  const [layoutSel, setLayoutSel] = usePersistentState<Record<string, string>>(
    storage,
    PKEY.layoutSel,
    {},
    isStrRecord,
  );
  // an upload's only durable truth is its character-space text plus a name; every decode is derived
  // through the active lens, never stored.
  const [uploaded, setUploaded] = usePersistentState<{ text: string; name: string } | null>(
    storage,
    PKEY.profile,
    null,
    isUpload,
  );
  // a simple Game's uploaded "my own" Profile, per Game (Destiny's cvars.xml). Same durable truth as
  // the SC2 `uploaded` slot - character-space text + name, re-ingested per view through the Game's
  // own parser - but keyed by gameId so each simple Game keeps its own upload (SC2 rides its Source
  // model, so it is not in here).
  const [uploadedProfile, setUploadedProfile] = usePersistentState<
    Record<string, { text: string; name: string }>
  >(storage, PKEY.profileUpload, {}, isUploadRecord);
  // the command card selection: mode gates which contexts are offered; race plus the
  // selected unit or structure pick the card. The raw pick is kept and validity is derived, so a
  // pick hidden by a mode switch revives when its mode returns instead of being reset.
  const [mode, setMode] = usePersistentState(storage, PKEY.mode, "multiplayer", isStr);
  const [race, setRace] = usePersistentState(storage, PKEY.race, "terran", isStr);
  const [contextSel, setContextSel] = usePersistentState(storage, PKEY.context, "none", isStr);
  // the flat-Context selection per Game (Destiny's On Foot / Vehicle), same per-Game record shape as
  // profileSel/layoutSel. A stale id derives back to the Game's first Context; SC2 ignores this.
  const [flatContextSel, setFlatContextSel] = usePersistentState<Record<string, string>>(
    storage,
    PKEY.flatContext,
    {},
    isStrRecord,
  );
  return {
    boardId,
    setBoardId,
    gameId,
    setGameId,
    sc2SourceId,
    setSc2SourceId,
    profileSel,
    setProfileSel,
    imported,
    setImported,
    layoutSel,
    setLayoutSel,
    uploaded,
    setUploaded,
    uploadedProfile,
    setUploadedProfile,
    mode,
    setMode,
    race,
    setRace,
    contextSel,
    setContextSel,
    flatContextSel,
    setFlatContextSel,
  };
}
